import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, TrendingUp, Clock, CheckCircle, Banknote } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api("get", "/admin/dashboard")
      .then((res) => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api]);

  if (loading)
    return <div className="animate-pulse text-muted-foreground text-sm">Memuat dashboard...</div>;

  const cards = [
    { label: "Total Revenue", value: `Rp ${(stats?.total_revenue || 0).toLocaleString("id-ID")}`, icon: Banknote, id: "stat-revenue" },
    { label: "Total Payments", value: stats?.total_payments || 0, icon: TrendingUp, id: "stat-total" },
    { label: "Pending", value: stats?.pending_payments || 0, icon: Clock, id: "stat-pending" },
    { label: "Paid", value: stats?.paid_payments || 0, icon: CheckCircle, id: "stat-paid" },
    { label: "Today", value: stats?.today_payments || 0, icon: ArrowRight, id: "stat-today" },
  ];

  const statusColor = { paid: "text-green-700", pending: "text-amber-600", expired: "text-red-600" };

  return (
    <div data-testid="dashboard-page" className="space-y-6 sm:space-y-8">
      <div className="flex flex-col gap-4 rounded-3xl border border-border/70 bg-card/90 p-6 shadow-sm sm:p-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Overview</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight sm:text-5xl">Dashboard</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">Ringkasan pembayaran, aktivitas hari ini, dan akses API key di satu layar.</p>
        </div>
        <div className="rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm text-muted-foreground">
          Workspace ini memakai 1 admin login dan banyak API key untuk banyak project.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.id} className="rounded-3xl border border-border/70 bg-card/90 p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{c.label}</span>
              <c.icon size={16} className="text-muted-foreground" />
            </div>
            <p className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          data-testid="quick-create-payment"
          onClick={() => navigate("/payments")}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
        >
          Buat Payment <ArrowRight size={14} />
        </button>
        <button
          data-testid="quick-view-history"
          onClick={() => navigate("/history")}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm font-semibold transition hover:bg-secondary"
        >
          Lihat History
        </button>
      </div>

      <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <h3 className="font-heading text-lg font-bold tracking-tight">Payment Terakhir</h3>
        </div>
        {stats?.recent_payments?.length > 0 ? (
          <div className="divide-y divide-border">
            {stats.recent_payments.map((p) => (
              <div key={p.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="min-w-0">
                  <p className="text-sm font-medium font-mono">{p.id.slice(0, 12)}...</p>
                  <p className="text-xs text-muted-foreground">{p.description || "No description"}</p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-sm font-semibold font-mono">
                    Rp {(p.unique_amount || p.amount || 0).toLocaleString("id-ID")}
                  </p>
                  <span className={`text-xs font-bold uppercase ${statusColor[p.status] || ""}`}>
                    {p.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">Belum ada payment</div>
        )}
      </div>
    </div>
  );
}
